import type {
  BaccaratOutcome,
  BaccaratSpot,
  BalanceSeries,
  Card,
  CasinoLeaderboardEntry,
  CasinoSnapshot,
  PocketColor,
  PublicCard,
  PublicHoldemAction,
  PublicSeat,
  RoomState,
  CrapsBetId,
  CrapsRoll,
  CrapsRules,
  RoulettePocket,
  RouletteWheel,
  ThreecardOutcome,
} from '@tv-casino/shared';
import {
  MAX_SEATS,
  SIDE_BETS,
  describeKenoTicket,
  gameLabel,
  isHardRoll,
  pocketColor,
  profileHex,
  profileInitials,
  rollTotal,
  wheelOrder,
} from '@tv-casino/shared';
import type { CrapsSpotMark } from '../components/crapsFelt.js';
import { deckShoe, type DeckShoeVM } from '../components/deckShoeModel.js';
import { gamePollModel, type GamePollVM } from '../components/gamePollModel.js';
import { kenoTicketColorIndex } from '../components/kenoBoardModel.js';
import { crapsMarks, crapsRollCall, crapsStacks, type CrapsStackMap } from '../components/crapsFelt.js';
import {
  rouletteStacks,
  rouletteWinMarks,
  type RouletteSpotMark,
  type RouletteStackMap,
} from '../components/rouletteFelt.js';
import { boardDeskModel, type MemberMarketVM } from '../components/memberMarketModel.js';
import { formatSignedChips } from '../lib/format.js';
import { hexAlpha, hexHighlight, seatHues } from '../lib/playerColors.js';
import {
  baccaratSideBetToken,
  blackjackPayoutLabel,
  pairPlusToken,
  sideBetToken,
  type PayoutTone,
} from '../lib/payouts.js';
import { seatWins, winHeadline, type SeatWin, type WinHeadline } from './winModel.js';

/**
 * The pure view-model for the TV route: status narration, per-seat plates,
 * dealer row and join-code/URL derivation — everything Host.tsx shows,
 * computed from (state, casino, urls) with no React or sockets involved.
 */

export interface HostSeatHand {
  cardsFaceUp: (Card | undefined)[];
  mark: string;
  pop: boolean;
  /** Unique per hand within the seat, and changes when the hand settles. */
  key: string;
}

/** One blackjack side-bet plaque beside a seat's bet spot: felt, not prose. */
export interface HostSideBetPlaque {
  name: string;
  stake: number;
  /** The settle mark — signed chips or `push`; null while still riding. */
  mark: string | null;
  tone: PayoutTone;
}

export interface HostSeatRow {
  index: number;
  name: string | null;
  chips: number;
  connected: boolean;
  taken: boolean;
  isHost: boolean;
  active: boolean;
  badges: string[];
  /** The one line under a Hold'em hand (showdown label, fold, shove or win). */
  mark: string;
  markPop: boolean;
  markKey: string;
  /** Face-up Hold'em hole cards while revealed, else null. */
  cardsFaceUp: (Card | undefined)[] | null;
  statusLabel: string;
  committed: number;
  /** Blackjack hands on the felt, with their marks. */
  hands: HostSeatHand[] | null;
  /** Blackjack side bets riding on the main wager: one plaque each. */
  sideBets: HostSideBetPlaque[];
  /** Baccarat spot colour on the bet chip, when the seat has backed one. */
  bacSpot: BaccaratSpot | null;
  /**
   * How this seat's chips should move on the felt: pushed out as a stack
   * lands, paid back on a win, swept on a loss. Null when nothing moves.
   */
  chipMotion: 'placing' | 'won' | 'lost' | null;
  /** Changes whenever the chips should move again, so the motion replays. */
  chipKey: string;
  /** What this seat just won, for the coins and the rising amount; null otherwise. */
  win: SeatWin | null;
  /**
   * Who sits here, in colour: the seat's resolved hue (a profile pick when it
   * is free, else a by-index one), its lit top for the avatar disc, and a
   * faint wash for the plate. The same hue the felt's chips wear.
   */
  hue: string;
  hueLight: string;
  hueWash: string;
  /** Up to two letters for the avatar disc. */
  initials: string;
  /** A signed-in account sits here: the plate carries the profile badge. */
  hasProfile: boolean;
}

export interface HostCoupHand {
  spot: 'player' | 'banker';
  label: string;
  cardsFaceUp: (Card | undefined)[];
  total: string | null;
  won: boolean;
  emptyLabel: string;
}

export interface HostDealerRow {
  holdem: boolean;
  baccarat: boolean;
  roulette: boolean;
  craps: boolean;
  keno: boolean;
  label: string;
  cardsFaceUp: (Card | undefined)[];
  valueLine: string | null;
  emptyLabel: string;
  potLabel: string | null;
  coupHands: HostCoupHand[] | null;
}

/** One table in the TV's between-games lineup. */
export interface HostTableRow {
  id: string;
  name: string;
  gameLabel: string;
  live: boolean;
  closed: boolean;
  note: string;
}

/** One leaderboard line: rank, who, and how they stand. */
export interface HostLeaderRow {
  memberId: string;
  rank: number;
  name: string;
  /**
   * The hue this member picked on their profile, as a colour to paint the
   * dot beside their name. Null when they never picked one — the board then
   * prints the name alone rather than inventing a colour for them.
   */
  accentHex: string | null;
  /** A signed-in account has played under this membership. */
  hasProfile: boolean;
  netLabel: string;
  handsLabel: string;
  tone: 'win' | 'loss' | 'even';
  /** "+120 best · −60 worst" — the night's extremes, null before a settle. */
  extremesLabel: string | null;
  /** Everything they pushed out: "840 out". */
  wageredLabel: string | null;
  /** The run they are on right now: "3 straight wins". Null when even. */
  streakLabel: string | null;
}

/**
 * The TV's house board: what the house has open and who is up. Shown while
 * the table is empty, or whenever the host puts the leaderboard on the cast.
 */
export interface HostLobbyBoard {
  casinoName: string;
  tables: HostTableRow[];
  leaders: HostLeaderRow[];
  desks: MemberMarketVM[];
  /** Shown instead of the rows before anyone has played a hand. */
  emptyLine: string | null;
  /**
   * True when the host forced this board over a live table. The empty-table
   * house board is the other path; both share the same rows.
   */
  forced: boolean;
}

/**
 * How many leader rows fit beside the join card on a TV without clipping.
 * A third chip wraps under and gets cut by the table's `overflow: hidden`.
 */
export const LEADERBOARD_PAGE_SIZE = 2;

/**
 * How many leader rows fit when the house board owns the stage — empty table
 * or a host-forced board, with no felt competing for height.
 */
export const LEADERBOARD_STAGE_PAGE_SIZE = 5;

/** Dwell between leaderboard carousel pages on the TV. */
export const LEADERBOARD_PAGE_MS = 5_000;

/** One page of the TV leaderboard carousel. */
export interface LeaderboardPage<T> {
  visible: readonly T[];
  page: number;
  pageCount: number;
  /** True when the board has to cycle to show everyone. */
  cycling: boolean;
}

/**
 * Slice the night's standings into TV-sized pages. `tick` advances under a
 * timer on the Host; the wrap matches the landing reel so a negative index
 * still lands on a real page.
 */
export function leaderboardPage<T>(
  leaders: readonly T[],
  tick: number,
  pageSize: number = LEADERBOARD_PAGE_SIZE,
): LeaderboardPage<T> {
  const size = Math.max(1, pageSize);
  const pageCount = Math.max(1, Math.ceil(leaders.length / size));
  const page = ((tick % pageCount) + pageCount) % pageCount;
  const start = page * size;
  return {
    visible: leaders.slice(start, start + size),
    page,
    pageCount,
    cycling: pageCount > 1,
  };
}

export interface HostVM {
  statusLine: string;
  seatRows: HostSeatRow[];
  /**
   * The seats the felt draws. Open seats only show in the lobby; once a round
   * is on, the arc closes up around the players so their cards get the room.
   */
  shownSeatRows: HostSeatRow[];
  /** Open seats are folded away: the arc sizes itself to the players. */
  seatsClosedUp: boolean;
  dealerRow: HostDealerRow;
  /** The street status line while a Hold'em hand is live, else null. */
  holdemLine: string | null;
  holdemNarration: string | null;
  holdemResultLine: string;
  joinCodeLabel: string;
  joinUrl: string;
  /** Protocol-stripped URL the join chip prints under the QR. */
  joinUrlShort: string;
  castUrl: string | null;
  castBlocked: string | null;
  tableActive: boolean;
  banner: string | null;
  kicker: string;
  /** Who runs the table, for the host plate in the header. */
  hostName: string | null;
  /** The host is signed in: their header plate carries the badge too. */
  hostHasProfile: boolean;
  /**
   * The table's sound is off, for the header's muted chip. The web draws a
   * sound button that both reports and flips it; a TV has no pointer, so it
   * only reports — the phone's host drawer is where it flips.
   */
  muted: boolean;
  feltText: string;
  ruleLine: string;
  shoeLabel: string;
  /**
   * The spin or roll count, for games that keep a history strip on the felt
   * (roulette, craps): the count prints beside that strip instead of in the
   * shoe corner, where it fought the layout for room. Null for the rest.
   */
  historyLabel: string | null;
  /** The deck on the felt, for the games that deal cards; null for the rest. */
  shoe: DeckShoeVM | null;
  seatedCount: number;
  maxSeats: number;
  payoutNote: boolean;
  /** Who deals next, printed under the felt during the payout break. */
  payoutNoteLine: string | null;
  /**
   * What the insurance wagers did on this settle — one line for the rail,
   * e.g. "Insurance · Ada +30 · Bob −10". Null when none rode.
   */
  insuranceLine: string | null;
  /** The big win the felt stops for on this settle, or null. */
  winHeadline: WinHeadline | null;
  isHoldem: boolean;
  isBaccarat: boolean;
  isRoulette: boolean;
  isCraps: boolean;
  isThreecard: boolean;
  isKeno: boolean;
  /** Past coups on the felt, oldest first, last 24. */
  beadRoad: { outcome: BaccaratOutcome; letter: string; label: string }[];
  /** Past three-card hands, oldest first, last 24. */
  threecardRoad: { key: string; outcome: ThreecardOutcome; letter: string; label: string }[];
  /** The roulette wheel and layout, when the felt is roulette. */
  roulette: {
    /** Which wheel the table spins — it decides the layout's zero corner. */
    wheel: RouletteWheel;
    /** Pockets in physical wheel order — the ring is drawn in this order. */
    wheelOrder: readonly RoulettePocket[];
    /** Index of the landed pocket in `wheelOrder`, or null before the spin. */
    landedIndex: number | null;
    pocket: string | null;
    /** The settled pocket, for the dolly the layout marks — null mid-roll. */
    pocketValue: RoulettePocket | null;
    pocketColor: PocketColor | null;
    /** Every stack on the layout, spot by spot — nobody's is "mine" on the TV. */
    stacks: RouletteStackMap;
    /** Spots the settled pocket pays, for the dolly-and-flash — empty mid-roll. */
    marks: Partial<Record<string, RouletteSpotMark>>;
    /** Past pockets, oldest first, capped for the ribbon. */
    pocketRoad: { pocket: string; color: PocketColor }[];
    /** The hot/cold board, once enough spins have run to mean anything. */
    board: HostPocketBoard | null;
    resultLine: string | null;
  } | null;
  /** The dice, when the felt is craps. */
  craps: {
    /** The table's rules: which groups of the layout are printed at all. */
    rules: CrapsRules;
    /** Every chip on the felt, spot by spot — the TV owns no seat, so none are "mine". */
    stacks: CrapsStackMap;
    /** What the last roll did, spot by spot, for the flash that follows it. */
    marks: Partial<Record<CrapsBetId, CrapsSpotMark>>;
    /** The point, or null on the come-out. */
    point: number | null;
    /** "COME OUT" or "POINT 6" — what the puck says. */
    puckLabel: string;
    /** Puck ON is the point phase; OFF is the come-out. */
    puckOn: boolean;
    /** The dice as they last landed, for the big pair on the felt. */
    dice: CrapsRoll | null;
    total: number | null;
    /** The stickman's call on the last roll. */
    callLine: string | null;
    shooterSeatIndex: number | null;
    shooterName: string | null;
    /** Rolls this hand — the "hot hand" counter. */
    rollNumber: number;
    /** Recent rolls, oldest first, for the ribbon. */
    rollRibbon: { key: string; total: number; dice: CrapsRoll; hard: boolean }[];
    resultLine: string | null;
    /** The night at a glance: the record hand, and what the dice keep landing. */
    stats: {
      /** The longest hand of the session, or null before the first one ends. */
      longest: { name: string | null; rolls: number } | null;
      /** The most-landed totals, once enough rolls have run to mean anything. */
      hot: { total: number; count: number }[];
    };
  } | null;
  /** The keno board, when the felt is keno. */
  keno: {
    /** The draw the cells light up, or null before the draw. */
    draw: number[] | null;
    /** True once the draw has settled: catches flash and results show. */
    settled: boolean;
    /**
     * The server's settle deadline, for the ball-by-ball reveal. The board
     * runs its clock off this so the TV and the phones call balls together.
     */
    endsAt: number | null;
    /** Lounge-card hues when the host asked for them; otherwise seat colours. */
    classicColors: boolean;
    /** Every ticket at the table, seat order, each in its table colour. */
    tickets: { spots: number[]; colorIndex: number }[];
    /** Per-seat slates, for the ticket rail beside the board. */
    seatSlates: {
      seatIndex: number;
      name: string | null;
      done: boolean;
      staked: number;
      tickets: {
        id: number;
        label: string;
        amount: number;
        payout: number | null;
        catches: number[];
        colorIndex: number;
      }[];
    }[];
    /** Past draws, oldest first, capped — the heat board's source. */
    history: number[][];
    /** The most-drawn numbers across the history window, best first. */
    hot: { spot: number; count: number }[];
  } | null;
  /** Table lineup + leaderboard, shown between games under a casino. */
  lobbyBoard: HostLobbyBoard | null;
  /**
   * The table's open vote on the next game, or null. The TV only ever watches
   * it — the ballots come off the phones.
   */
  gamePoll: GamePollVM | null;
  /**
   * Host put the leaderboard on the cast. The TV swaps the felt for the board
   * while this is true; empty tables already show the board without it.
   */
  leaderboardShown: boolean;
  /**
   * Host put the large join QR on the cast. Empty tables already show it;
   * this is how the host brings it back mid-session.
   */
  qrShown: boolean;
  /** Large join card on stage: empty table, or the host asked for the QR. */
  showJoinCard: boolean;
  /**
   * Live felt. Off while nobody is seated, and while the host has the
   * leaderboard or the large QR on stage.
   */
  showFelt: boolean;
  /**
   * The lobby band owns the body (full-stage board / QR) instead of sitting
   * as a strip over a shrunken felt.
   */
  stageLobby: boolean;
}

/** One pocket's standing on the hot/cold board. */
export interface HostPocketTally {
  pocket: string;
  color: PocketColor;
  /** Times it landed in the window. */
  count: number;
  /** Spins since it last landed — 0 is the spin just gone, null is never. */
  since: number | null;
}

/**
 * The hot/cold board. Pure bookkeeping over past pockets: a wheel has no
 * memory, and the board says so out loud rather than implying a due number.
 */
export interface HostPocketBoard {
  /** Spins in the window the board counts over. */
  spins: number;
  hot: HostPocketTally[];
  cold: HostPocketTally[];
  /** Red/black/zero, odd/even, low/high across the same window. */
  trends: { label: string; count: number; pct: number }[];
}

/**
 * A short history says nothing at all — three spins would crown a "hot"
 * number off one landing — so the board stays dark until the night has run
 * enough spins for the counts to be worth reading.
 */
const MIN_BOARD_SPINS = 12;

/** How many pockets each end of the board shows. */
const BOARD_ENDS = 5;

function pocketBoard(
  history: readonly RoulettePocket[],
  wheel: readonly RoulettePocket[],
): HostPocketBoard | null {
  if (history.length < MIN_BOARD_SPINS) return null;

  const tallies: HostPocketTally[] = wheel.map((pocket) => {
    const seen = history.reduce<number>((n, p) => (p === pocket ? n + 1 : n), 0);
    const last = history.lastIndexOf(pocket);
    return {
      pocket: String(pocket),
      color: pocketColor(pocket),
      count: seen,
      since: last === -1 ? null : history.length - 1 - last,
    };
  });

  // Never-seen sorts as longer ago than anything actually seen.
  const age = (t: HostPocketTally) => t.since ?? Number.POSITIVE_INFINITY;
  const hot = [...tallies]
    .sort((a, b) => b.count - a.count || age(a) - age(b))
    .slice(0, BOARD_ENDS)
    .filter((t) => t.count > 0);
  const onBoard = new Set(hot.map((t) => t.pocket));
  const cold = tallies
    .filter((t) => !onBoard.has(t.pocket))
    .sort((a, b) => a.count - b.count || age(b) - age(a))
    .slice(0, BOARD_ENDS);

  const share = (fits: (p: RoulettePocket) => boolean) =>
    history.reduce<number>((n, p) => (fits(p) ? n + 1 : n), 0);
  const trend = (label: string, fits: (p: RoulettePocket) => boolean) => {
    const count = share(fits);
    return { label, count, pct: Math.round((count / history.length) * 100) };
  };
  const notZero = (p: RoulettePocket): p is number => p !== 0 && p !== '00';

  return {
    spins: history.length,
    hot,
    cold,
    trends: [
      trend('Red', (p) => pocketColor(p) === 'red'),
      trend('Black', (p) => pocketColor(p) === 'black'),
      trend('Zero', (p) => pocketColor(p) === 'green'),
      trend('Odd', (p) => notZero(p) && p % 2 === 1),
      trend('Even', (p) => notZero(p) && p % 2 === 0),
      trend('1–18', (p) => notZero(p) && p <= 18),
      trend('19–36', (p) => notZero(p) && p >= 19),
    ],
  };
}

/**
 * A handful of rolls would crown a "hot" number off one landing, so the dice
 * board stays dark until the session has run enough of them to read.
 */
const MIN_BOARD_ROLLS = 12;

/** How many totals the dice board shows. */
const HOT_ENDS = 3;

/** The totals the dice have favored, best first, from the session tally. */
export function crapsHotBoard(tally: readonly number[]): { total: number; count: number }[] {
  const rolls = tally.reduce((n, count) => n + count, 0);
  if (rolls < MIN_BOARD_ROLLS) return [];
  return tally
    .map((count, total) => ({ total, count }))
    .filter((t) => t.total >= 2 && t.count > 0)
    .sort((a, b) => b.count - a.count || a.total - b.total)
    .slice(0, HOT_ENDS);
}

export interface HostModelOptions {
  casino?: CasinoSnapshot | null;
  /**
   * The venue code this screen was opened with. A private venue's snapshot
   * redacts `joinCode`, so nothing printable may come from here — it exists
   * for the Cast URL, which the receiver loads and no onlooker ever sees.
   */
  casinoCode?: string | null;
  /** Permanent code of the casino table pinned to this TV. */
  tableCode?: string | null;
  /** The URL phones/cast devices should reach; localhost is rewritten by /api/config. */
  publicUrl?: string;
  displayToken?: string | null;
  /** The room_closed reason, while the banner shows. */
  closed?: string | null;
  /** Best net first, as the public leaderboard endpoint returns it. */
  leaderboard?: CasinoLeaderboardEntry[] | null;
  /** Public balance lines, keyed into chip desks by memberId. */
  timeline?: BalanceSeries[] | null;
}

const LOCAL_HOST = /localhost|127\.0\.0\.1|\[::1\]/;

/** Street names as the felt announces them. */
const STREET_LABELS: Record<string, string> = {
  preflop: 'Hole cards',
  flop: 'Flop',
  turn: 'Turn',
  river: 'River',
};

/** Present-tense verbs for narrating the last action. */
const ACTION_VERBS: Record<PublicHoldemAction['action'], string> = {
  fold: 'folds',
  check: 'checks',
  call: 'calls',
  bet: 'bets',
  raise: 'raises to',
  all_in: 'is all in',
  post_blind: 'posts the blind',
  post_ante: 'posts the ante',
};

type HoldemView = NonNullable<RoomState['holdem']>;
type HoldemSeatView = HoldemView['seats'][number];
type PublicHand = RoomState['seats'][number]['hands'][number];

const SPOT_LABELS: Record<BaccaratSpot, string> = {
  player: 'Player',
  banker: 'Banker',
  tie: 'Tie',
};

const BEAD_LETTERS: Record<BaccaratOutcome, string> = {
  player: 'P',
  banker: 'B',
  tie: 'T',
};

/** One letter per past hand, for the TV's history strip. */
const OUTCOME_LETTERS: Record<ThreecardOutcome, string> = {
  ante: 'A',
  play: 'P',
  dealer: 'D',
  push: '=',
  fold: 'F',
};

const OUTCOME_NAMES: Record<ThreecardOutcome, string> = {
  ante: 'Ante',
  play: 'Play',
  dealer: 'Dealer',
  push: 'Push',
  fold: 'Fold',
};

const faceUpOnly = (cards: readonly PublicCard[]): (Card | undefined)[] =>
  cards.map((c) => (c.faceUp ? c.card : undefined));

/** Short per-hand mark: value while live, outcome once settled. */
export const handMark = (hand: PublicHand): string => {
  if (hand.result) {
    const net = hand.payout != null ? hand.payout - hand.bet : null;
    const name =
      hand.result === 'blackjack'
        ? 'Blackjack!'
        : hand.result === 'win'
          ? 'Win'
          : hand.result === 'push'
            ? 'Push'
            : hand.result === 'surrender'
              ? 'Surrender'
              : 'Loss';
    return net != null && net !== 0 ? `${name} ${formatSignedChips(net)}` : name;
  }
  const value = hand.visibleValue ?? '…';
  if (hand.status === 'busted') return `${value} bust`;
  if (hand.status === 'blackjack') return `${value} blackjack!`;
  return String(value);
};

/** Label for a seat with no cards on the felt. */
export const seatStatusLabel = (seat: PublicSeat, state: RoomState | null): string => {
  if (!seat.connected) return 'Reconnecting…';
  if (state?.phase === 'lobby' || state?.phase === 'payout') {
    return seat.lobbyReady ? 'In' : 'Sitting out';
  }
  if (seat.status === 'betting') return 'Betting…';
  if (seat.status === 'ready') return 'Bet placed';
  if (seat.status === 'waiting') return 'Waiting…';
  return 'Sitting out';
};

/** The one line under a Hold'em hand: showdown label, fold, shove or win. */
export const holdemSeatMark = (hs: HoldemSeatView): string => {
  if (hs.revealed && hs.handLabel) {
    const chips = hs.won != null && hs.won > 0 ? ` · ${formatSignedChips(hs.won)}` : '';
    return `${hs.handLabel}${chips}`;
  }
  if (hs.won != null && hs.won > 0) return `Won ${formatSignedChips(hs.won)}`;
  if (hs.folded) return 'Folded';
  return hs.allIn ? 'All in' : '';
};

/** Button / blind pips shown on the seat plate during a Hold'em hand. */
export const holdemBadges = (holdem: HoldemView | null, seatIndex: number): string[] => {
  if (!holdem || holdem.street === null) return [];
  const badges: string[] = [];
  if (holdem.buttonSeatIndex === seatIndex) badges.push('D');
  if (holdem.smallBlindSeatIndex === seatIndex) badges.push('SB');
  if (holdem.bigBlindSeatIndex === seatIndex) badges.push('BB');
  return badges;
};

export function hostModel(
  state: RoomState | null,
  opts: HostModelOptions = {},
): HostVM {
  const {
    casino = null,
    casinoCode = null,
    tableCode = null,
    publicUrl = '',
    displayToken = null,
    closed = null,
    leaderboard = null,
    timeline = null,
  } = opts;
  const url = publicUrl;

  const holdem = state?.holdem ?? null;
  const baccarat = state?.baccarat ?? null;
  const isHoldem = state?.gameType === 'holdem' && holdem !== null;
  const isBaccarat = state?.gameType === 'baccarat' && baccarat !== null;
  const rouletteState = state?.roulette ?? null;
  const isRoulette = state?.gameType === 'roulette' && rouletteState !== null;
  const crapsState = state?.craps ?? null;
  const isCraps = state?.gameType === 'craps' && crapsState !== null;
  const threecardState = state?.threecard ?? null;
  const isThreecard = state?.gameType === 'threecard' && threecardState !== null;
  const kenoState = state?.keno ?? null;
  const isKeno = state?.gameType === 'keno' && kenoState !== null;
  const seated = state?.seats.filter((s) => s.playerId !== null) ?? [];
  const tableActive = seated.length > 0;
  const host = state?.players.find((p) => p.isHost) ?? null;
  const dealerValue = state?.dealer.visibleValue ?? null;

  const seatName = (index: number): string =>
    state?.seats[index]?.name ?? `seat ${index + 1}`;

  /** "Ada raises to 40", for the status line. */
  const holdemNarration = (): string | null => {
    if (!holdem?.lastAction) return null;
    const { seatIndex, action, amount } = holdem.lastAction;
    const chips = action === 'fold' || action === 'check' ? '' : ` ${amount.toLocaleString()}`;
    return `${seatName(seatIndex)} ${ACTION_VERBS[action]}${chips}`;
  };

  /** Winners and their hands once the pots are pushed. */
  const holdemResultLine = (): string => {
    if (!holdem || !holdem.results || holdem.results.length === 0) return 'Hand settled.';
    return holdem.results
      .map((r) => {
        const who = r.winners.map(seatName).join(' & ');
        const pot = r.potIndex === 0 ? '' : ` (side pot ${r.potIndex})`;
        return holdem.wonByFold
          ? `${who} takes ${r.amount.toLocaleString()}${pot}`
          : `${who} wins ${r.amount.toLocaleString()}${pot} with ${r.label ?? 'the best hand'}`;
      })
      .join(' · ');
  };

  /** Status during the four betting streets. */
  const holdemStreetLine = (): string => {
    if (!holdem) return 'Cards are live.';
    const parts: string[] = [];
    const narration = holdemNarration();
    if (narration) parts.push(narration);
    if (state?.activeSeatIndex != null) {
      const hs = holdem.seats[state.activeSeatIndex];
      const owed = hs ? Math.max(0, holdem.currentBet - hs.committed) : 0;
      parts.push(
        owed > 0
          ? `${seatName(state.activeSeatIndex)} faces ${owed.toLocaleString()}`
          : `${seatName(state.activeSeatIndex)} to act`,
      );
    }
    if (parts.length === 0) parts.push(`Pot ${holdem.potTotal.toLocaleString()}`);
    return parts.join(' — ');
  };

  const statusLine = (): string => {
    if (!state) return '';
    switch (state.phase) {
      case 'lobby': {
        if (seated.length === 0) {
          return casino
            ? 'Waiting for the first player — whoever takes a seat deals from their phone.'
            : 'Waiting for the first phone to join — that player runs the table.';
        }
        const inCount = seated.filter((s) => s.lobbyReady).length;
        const who = inCount === seated.length ? 'Everyone is in' : `${inCount} of ${seated.length} in`;
        if (isKeno || isRoulette || isCraps) {
          return `${who} — the betting window opens on its own.`;
        }
        return `${who} — ${host?.name ?? 'the host'} deals from their phone.`;
      }
      case 'betting':
        return isKeno
          ? 'Tap your spots — the house draws twenty once every seat locks in.'
          : isRoulette
          ? 'Place your chips — the wheel spins once every seat locks in.'
          : isBaccarat
            ? 'Back Player, Banker or Tie — the coup deals once everyone is in.'
            : isCraps
              ? 'Come-out betting — the dice go out once every seat is ready.'
              : isThreecard
                ? 'Post your antes — the cards come out once everyone is in.'
                : 'Waiting for bets — cards deal automatically once everyone is in.';
      case 'rolling':
        return crapsRollLine();
      case 'insurance':
        return 'Dealer shows an ace — insurance decisions at the table.';
      case 'playing':
        return isThreecard
          ? threecardDecisionLine()
          : dealerValue !== null
            ? `Everyone is playing against the dealer's ${dealerValue}.`
            : 'Cards are live.';
      case 'dealer':
        return isKeno
          ? 'No more bets — the balls are falling…'
          : isRoulette
          ? 'No more bets — the ball is rolling…'
          : isBaccarat
            ? 'Dealing the coup…'
            : isThreecard
              ? 'The dealer turns over…'
              : 'Dealer plays…';
      case 'preflop':
      case 'flop':
      case 'turn':
      case 'river':
        return holdemStreetLine();
      case 'payout':
        return isHoldem
          ? holdemResultLine()
          : isKeno
            ? (kenoResultLine() ?? 'Draw settled.')
            : isRoulette
            ? (rouletteResultLine() ?? 'Spin settled.')
            : isBaccarat
              ? baccaratResultLine()
              : isCraps
                ? (crapsResultLine() ?? 'Hand settled.')
                : isThreecard
                  ? (threecardResultLine() ?? 'Hand settled.')
                  : 'Round settled.';
    }
  };

  /** Who still holds their play-or-fold, mid-hand. */
  const threecardDecisionLine = (): string => {
    if (!threecardState) return 'Cards are live.';
    const deciding = (state?.activeSeatIndex != null)
      ? [state.activeSeatIndex]
      : [];
    const decidingName = deciding.length > 0 ? seatName(deciding[0]!) : null;
    const played = threecardState.seats.filter((ts) => ts.play > 0).length;
    const folded = threecardState.seats.filter((ts) => ts.folded).length;
    const parts: string[] = [];
    if (decidingName) parts.push(`${decidingName} to play or fold`);
    if (played > 0) parts.push(`${played} in`);
    if (folded > 0) parts.push(`${folded} mucked`);
    return parts.length > 0 ? parts.join(' · ') : 'The cards are out.';
  };

  const threecardResultLine = (): string | null => {
    if (!threecardState?.results) return null;
    const dealer = threecardState.dealerLabel
      ? `Dealer ${threecardState.dealerLabel}${
          threecardState.dealerQualifies === false ? ' (doesn’t qualify)' : ''
        }`
      : 'Everyone mucked';
    const winners = threecardState.results
      .filter((r) => r.outcome === 'win')
      .map((r) => seatName(r.seatIndex))
      .join(', ');
    return winners ? `${dealer} · ${winners} win` : `${dealer} · the house holds`;
  };

  const baccaratResultLine = (): string => {
    if (!baccarat?.outcome) return 'Coup settled.';
    const headline =
      baccarat.outcome === 'tie' ? 'TIE' : `${SPOT_LABELS[baccarat.outcome].toUpperCase()} WINS`;
    const natural = baccarat.natural ? ' · natural' : '';
    const totals = `${baccarat.playerTotal ?? '?'} — ${baccarat.bankerTotal ?? '?'}`;
    const winners = (baccarat.results ?? [])
      .filter((r) => r.payout > r.stake)
      .map((r) => seatName(r.seatIndex))
      .join(', ');
    return winners
      ? `${headline}${natural} · ${totals} · ${winners}`
      : `${headline}${natural} · ${totals}`;
  };

  const rouletteResultLine = (): string | null => {
    if (!rouletteState?.pocket) return null;
    const pocket = String(rouletteState.pocket);
    const color = pocketColor(rouletteState.pocket).toUpperCase();
    const winners = (rouletteState.results ?? [])
      .filter((r) => r.payout > r.stake)
      .map((r) => seatName(r.seatIndex))
      .join(', ');
    return winners ? `${pocket} ${color} · ${winners} win` : `${pocket} ${color} · the house wins`;
  };

  const kenoResultLine = (): string | null => {
    if (!kenoState?.results || kenoState.results.length === 0) return null;
    const winners = kenoState.results
      .filter((r) => r.payout > r.stake)
      .map((r) => {
        const top = !r.topTicket
          ? ''
          : r.topTicket.catches === 0
            ? ' (caught none)'
            : ` (${r.topTicket.catches} caught)`;
        return `${seatName(r.seatIndex)}${top}`;
      });
    const pushes = kenoState.results
      .filter((r) => r.payout === r.stake)
      .map((r) => seatName(r.seatIndex));
    const pushLine = (names: string[]): string =>
      `${names.join(', ')} ${names.length === 1 ? 'pushes' : 'push'}`;
    if (winners.length > 0) {
      return `${winners.join(', ')} collect${pushes.length > 0 ? ` · ${pushLine(pushes)}` : ''}`;
    }
    if (!kenoState.results.some((r) => r.payout < r.stake)) return 'Stakes back — no winners';
    if (pushes.length > 0) return `${pushLine(pushes)} · no winners — the house takes the rest`;
    const dry = kenoState.seats
      .flatMap((ks) => ks.tickets)
      .every((t) => (t.catches ?? []).length === 0);
    return dry ? 'The board is dry — the house wins' : 'No winners — the house takes it';
  };

  /** What the table is watching for, mid-hand. */
  const crapsRollLine = (): string => {
    if (!crapsState) return 'The dice are out.';
    const shooter =
      crapsState.shooterSeatIndex != null ? seatName(crapsState.shooterSeatIndex) : 'The shooter';
    const rolled = crapsRollCall(crapsState);
    const call = rolled ? `${rolled} ` : '';
    return crapsState.point === null
      ? `${call}${shooter} is coming out.`
      : `${call}${shooter} is going for the ${crapsState.point}.`;
  };

  /** How the hand ended, and who it paid. */
  const crapsResultLine = (): string | null => {
    const call = crapsRollCall(crapsState);
    if (!call) return null;
    const winners = (crapsState?.results ?? [])
      .filter((r) => r.payout > r.stake)
      .map((r) => seatName(r.seatIndex))
      .join(', ');
    return winners ? `${call} · ${winners} collect` : `${call} · the house wins`;
  };

  /** One seat's craps line: what it has out, or what the hand paid it. */
  const crapsSeatMark = (seatIndex: number): string => {
    const cs = crapsState?.seats[seatIndex] ?? null;
    const result = crapsState?.results?.find((r) => r.seatIndex === seatIndex) ?? null;
    if (result) {
      const net = result.payout - result.stake;
      return net === 0 ? 'Even' : formatSignedChips(net);
    }
    if (!cs || cs.bets.length === 0) return '';
    const total = cs.bets.reduce((n, b) => n + b.amount, 0);
    return cs.ready && state?.phase === 'betting'
      ? `Ready · ${total.toLocaleString()}`
      : `${cs.bets.length} bet${cs.bets.length === 1 ? '' : 's'} · ${total.toLocaleString()}`;
  };

  /**
   * The felts where chips land and settle one stack at a time — roulette's
   * wheel, craps' roll-by-roll decisions — so their motion says something.
   * Blackjack and Hold'em move chips in one sweep, where the motion would
   * only repeat the mark the plate already prints.
   */
  const rouletteChipMotion = (
    seatIndex: number,
  ): { motion: HostSeatRow['chipMotion']; key: string } => {
    const result = rouletteState?.results?.find((r) => r.seatIndex === seatIndex) ?? null;
    const spin = rouletteState?.spinNumber ?? 0;
    if (result) {
      return { motion: result.payout > 0 ? 'won' : 'lost', key: `spin-${spin}-${seatIndex}` };
    }
    // The sliver is the stack of record; `seat.bet` only mirrors its total.
    const staked = (rouletteState?.seats[seatIndex]?.bets ?? []).reduce((n, b) => n + b.amount, 0);
    if (staked <= 0) return { motion: null, key: `idle-${spin}-${seatIndex}` };
    // Keyed on the running total, so every new stack replays the push.
    return { motion: 'placing', key: `stake-${spin}-${seatIndex}-${staked}` };
  };

  /** Keno rides roulette's beat: tickets push, a settled draw pays or sweeps. */
  const kenoChipMotion = (
    seatIndex: number,
  ): { motion: HostSeatRow['chipMotion']; key: string } => {
    const result = kenoState?.results?.find((r) => r.seatIndex === seatIndex) ?? null;
    const draw = kenoState?.drawNumber ?? 0;
    if (result) {
      return { motion: result.payout > 0 ? 'won' : 'lost', key: `draw-${draw}-${seatIndex}` };
    }
    const staked = (kenoState?.seats[seatIndex]?.tickets ?? []).reduce(
      (n, t) => n + t.amount,
      0,
    );
    if (staked <= 0) return { motion: null, key: `idle-${draw}-${seatIndex}` };
    // Keyed on the running total, so every new ticket replays the push.
    return { motion: 'placing', key: `stake-${draw}-${seatIndex}-${staked}` };
  };

  /** Craps settles per roll, so the chips move with every decision. */
  const crapsChipMotion = (
    seatIndex: number,
  ): { motion: HostSeatRow['chipMotion']; key: string } => {
    const round = state?.round ?? 0;
    const roll = crapsState?.rollNumber ?? 0;
    const result = crapsState?.results?.find((r) => r.seatIndex === seatIndex) ?? null;
    if (result) {
      const net = result.payout - result.stake;
      return { motion: net > 0 ? 'won' : net < 0 ? 'lost' : null, key: `hand-${round}-${seatIndex}` };
    }
    // What the last roll decided for this seat: a win lifts the stack, a loss
    // sweeps it, and a push or a mixed roll at least sends something back.
    const mine = (crapsState?.lastSettlements ?? []).filter((s) => s.seatIndex === seatIndex);
    if (mine.length > 0) {
      const net = mine.reduce((n, s) => n + s.payout - s.amount, 0);
      return { motion: net >= 0 ? 'won' : 'lost', key: `roll-${roll}-${seatIndex}` };
    }
    const staked = (crapsState?.seats[seatIndex]?.bets ?? []).reduce((n, b) => n + b.amount, 0);
    if (staked > 0) {
      // Keyed on the round and the running total only — a bet that rides from
      // roll to roll keeps its key, so the push does not replay every throw.
      return { motion: 'placing', key: `stake-${round}-${seatIndex}-${staked}` };
    }
    return { motion: null, key: `idle-${round}-${seatIndex}` };
  };

  const rouletteSeatMark = (seatIndex: number): string => {
    const rs = rouletteState?.seats[seatIndex] ?? null;
    if (!rs || rs.bets.length === 0) return '';
    const total = rs.bets.reduce((n, b) => n + b.amount, 0);
    const result = rouletteState?.results?.find((r) => r.seatIndex === seatIndex) ?? null;
    if (!result) return rs.done ? `Locked in · ${total.toLocaleString()}` : 'Placing…';
    if (result.payout === 0) return `Lost ${result.stake.toLocaleString()}`;
    return formatSignedChips(result.payout - result.stake);
  };

  const kenoSeatMark = (seatIndex: number): string => {
    const ks = kenoState?.seats[seatIndex] ?? null;
    if (!ks || ks.tickets.length === 0) return '';
    const total = ks.tickets.reduce((n, t) => n + t.amount, 0);
    const result = kenoState?.results?.find((r) => r.seatIndex === seatIndex) ?? null;
    if (!result) {
      return ks.done
        ? `Locked in · ${ks.tickets.length} ticket${ks.tickets.length === 1 ? '' : 's'}`
        : `${ks.tickets.length} ticket${ks.tickets.length === 1 ? '' : 's'} · ${total.toLocaleString()}`;
    }
    if (result.payout === 0) return `Lost ${result.stake.toLocaleString()}`;
    return formatSignedChips(result.payout - result.stake);
  };

  const baccaratSeatMark = (seatIndex: number): string => {
    const bs = baccarat?.seats[seatIndex] ?? null;
    if (!bs?.spot) return '';
    const result = baccarat?.results?.find((r) => r.seatIndex === seatIndex) ?? null;
    if (!result) return SPOT_LABELS[bs.spot];
    if (result.payout === result.stake) return `${SPOT_LABELS[bs.spot]} · push`;
    return `${SPOT_LABELS[bs.spot]} ${formatSignedChips(result.payout - result.stake)}`;
  };

  /** One seat's three-card line: what it is riding, or what it came back. */
  const threecardSeatMark = (seatIndex: number): string => {
    const ts = threecardState?.seats[seatIndex] ?? null;
    const ante = state?.seats[seatIndex]?.bet ?? 0;
    if (!ts || ante <= 0) return '';
    const result = threecardState?.results?.find((r) => r.seatIndex === seatIndex) ?? null;
    if (result) {
      // Pair Plus rides its own plaque, so the mark stays ante + play only —
      // the same split the phone's result line draws.
      const pairPlus = ts.pairPlus?.payout ?? 0;
      const net = result.payout - pairPlus - result.ante - result.play;
      return net === 0 ? 'Push' : formatSignedChips(net);
    }
    if (ts.folded) return 'Folded';
    if (ts.play > 0) return `Play ${ts.play.toLocaleString()}`;
    return state?.phase === 'playing' ? 'Deciding…' : `Ante ${ante.toLocaleString()}`;
  };

  const potLabel = holdem
    ? holdem.pots.length > 1
      ? holdem.pots
          .map((p, i) => `${i === 0 ? 'Main' : 'Side'} ${p.amount.toLocaleString()}`)
          .join(' · ')
      : `Pot ${holdem.potTotal.toLocaleString()}`
    : '';

  // A private venue sends no code at all — the snapshot's joinCode is already
  // redacted server-side, and the table's own code is a join code too, so the
  // chip, the URL and the QR all go dark rather than leaking it another way.
  const joinHidden = casino?.isPrivate === true;
  const joinCodeLabel = joinHidden ? '' : tableCode ?? state?.code ?? casino?.joinCode ?? '';
  const joinUrl = joinHidden
    ? ''
    : casino
      ? `${url}/play?casino=${casino.joinCode}${tableCode ? `&table=${tableCode}` : ''}`
      : state
        ? `${url}/play?room=${state.code}`
        : '';
  const joinUrlShort = joinHidden
    ? ''
    : `${url.replace(/^https?:\/\//, '')}${
        casino
          ? `/play?casino=${casino.joinCode}${tableCode ? `&table=${tableCode}` : ''}`
          : '/play'
      }`;
  // The receiver loads this itself, so it has to be a LAN address it can
  // reach. A private venue still casts: this URL goes over the Cast channel
  // and is never drawn, so it may carry the code the snapshot withholds.
  const castCasinoCode = casinoCode || casino?.joinCode || '';
  const castUrl = tableCode
    ? `${url}/tv?table=${tableCode}`
    : casino && castCasinoCode
      ? `${url}/tv?casino=${castCasinoCode}`
    : state && displayToken
      ? `${url}/host?code=${state.code}&display=${encodeURIComponent(displayToken)}`
      : null;
  const castBlocked = LOCAL_HOST.test(url)
    ? 'Cast devices can’t reach localhost. Run `pnpm tunnel` and open that URL, or use Chrome menu → Cast → Cast tab.'
    : castUrl === null
      ? 'Nothing to cast yet — open a table first.'
      : null;

  const sideBetNames = (state?.rules.sideBets ?? [])
    .map((id) => SIDE_BETS[id]?.name ?? null)
    .filter((name): name is string => name !== null);

  const wins = seatWins(state);
  const hueOf = seatHues(state);

  const seatRows: HostSeatRow[] = (state?.seats ?? []).map((seat) => {
    const isHost = state!.hostSeatIndex === seat.index;
    const occupant = state!.players.find((p) => p.playerId === seat.playerId) ?? null;
    const hue = hueOf(seat.index);
    const hs = isHoldem ? holdem?.seats[seat.index] ?? null : null;
    const bacSpot = isBaccarat ? baccarat?.seats[seat.index]?.spot ?? null : null;
    const bacMark = isBaccarat ? baccaratSeatMark(seat.index) : '';
    const rouMark = isRoulette ? rouletteSeatMark(seat.index) : '';
    const crapsMark = isCraps ? crapsSeatMark(seat.index) : '';
    const tcMark = isThreecard ? threecardSeatMark(seat.index) : '';
    const kenMark = isKeno ? kenoSeatMark(seat.index) : '';
    const committed = hs ? hs.committed : seat.bet;
    const mark = hs
      ? holdemSeatMark(hs)
      : bacMark || rouMark || crapsMark || tcMark || kenMark;
    const tcCards = isThreecard ? threecardState?.seats[seat.index]?.cards ?? [] : [];
    const tcSeat = isThreecard ? threecardState?.seats[seat.index] ?? null : null;
    const bacSeat = isBaccarat ? baccarat?.seats[seat.index] ?? null : null;
    const tcRevealed = isThreecard && Boolean(threecardState?.resolved) &&
      !threecardState?.seats[seat.index]?.folded && tcCards.length > 0;
    const chips = isRoulette
      ? rouletteChipMotion(seat.index)
      : isCraps
        ? crapsChipMotion(seat.index)
        : isKeno
          ? kenoChipMotion(seat.index)
          : { motion: null, key: `still-${seat.index}` };
    return {
      index: seat.index,
      name: seat.name,
      chips: seat.chips,
      connected: seat.connected,
      taken: seat.playerId !== null,
      isHost,
      // In craps the seat holding the dice is the one the table is watching.
      active:
        state!.activeSeatIndex === seat.index ||
        (isCraps && crapsState!.shooterSeatIndex === seat.index && state!.phase === 'rolling'),
      badges: holdemBadges(holdem ?? null, seat.index),
      mark,
      markPop: hs
        ? hs.won != null && hs.won > 0
        : Boolean(baccarat?.results) ||
          Boolean(rouletteState?.results) ||
          Boolean(crapsState?.results) ||
          Boolean(threecardState?.results) ||
          Boolean(kenoState?.results),
      markKey: hs
        ? String(hs.won ?? hs.handLabel ?? 'live')
        : bacMark || rouMark || crapsMark || tcMark || kenMark || String(seat.bet),
      // Three-card cards ride face-down until the showdown turns them over.
      cardsFaceUp:
        hs && hs.cards.length > 0
          ? faceUpOnly(hs.cards)
          : tcRevealed
            ? faceUpOnly(tcCards)
            : isThreecard && tcCards.length > 0
              ? tcCards.map(() => undefined)
              : null,
      statusLabel: seatStatusLabel(seat, state),
      committed,
      hands:
        hs === null && !isBaccarat && !isRoulette && !isCraps && !isThreecard && !isKeno && seat.hands.length > 0
          ? seat.hands.map((hand, i) => ({
              cardsFaceUp: faceUpOnly(hand.cards),
              mark: handMark(hand),
              pop: Boolean(hand.result),
              // Split hands sit side by side and often settle the same way, so
              // the seat position is what makes the key a hand's identity; the
              // result still rides along, so a settle remounts the mark.
              key: `${i}-${hand.result ?? 'live'}`,
            }))
          : null,
      sideBets:
        hs === null && !isRoulette && !isCraps && !isKeno
          ? isBaccarat
            ? (bacSeat?.sideBets ?? []).map(baccaratSideBetToken)
            : isThreecard
              ? tcSeat?.pairPlus && tcSeat.pairPlus.stake > 0
                ? [pairPlusToken(tcSeat.pairPlus)]
                : []
              : seat.sideBets.map(sideBetToken)
          : [],
      bacSpot,
      chipMotion: chips.motion,
      chipKey: chips.key,
      win: wins.find((w) => w.seatIndex === seat.index) ?? null,
      hue,
      hueLight: hexHighlight(hue),
      hueWash: hexAlpha(hue, 0.22),
      initials: profileInitials(seat.name ?? ''),
      hasProfile: occupant?.hasProfile ?? false,
    };
  });

  const dealerRow: HostDealerRow = state
    ? isHoldem
      ? {
          holdem: true,
          baccarat: false,
          roulette: false,
          craps: false,
          keno: false,
          label: holdem?.street ? STREET_LABELS[holdem.street] ?? 'Board' : 'Board',
          cardsFaceUp: faceUpOnly(holdem!.board),
          valueLine: null,
          emptyLabel:
            state.phase === 'payout'
              ? 'Hand settled'
              : state.phase === 'lobby'
                ? 'Waiting to deal'
                : 'Hole cards are out',
          potLabel: (holdem?.potTotal ?? 0) > 0 ? potLabel : null,
          coupHands: null,
        }
      : isRoulette
        ? {
            holdem: false,
            baccarat: false,
            roulette: true,
            craps: false,
            keno: false,
            label: 'Wheel',
            cardsFaceUp: [],
            valueLine: null,
            emptyLabel:
              state.phase === 'betting'
                ? 'Place your bets'
                : state.phase === 'dealer'
                  ? 'No more bets…'
                  : 'Spin settled',
            potLabel: null,
            coupHands: null,
          }
        : isBaccarat
          ? {
              holdem: false,
              baccarat: true,
              roulette: false,
              craps: false,
              keno: false,
              label: 'Coup',
              cardsFaceUp: [],
              valueLine: null,
              emptyLabel: state.phase === 'betting' ? 'Betting open' : 'Place your bets',
              potLabel: null,
              coupHands: (
                [
                  ['player', baccarat!.playerCards, baccarat!.playerTotal],
                  ['banker', baccarat!.bankerCards, baccarat!.bankerTotal],
                ] as const
              ).map(([spot, cards, total]) => ({
                spot,
                label: SPOT_LABELS[spot],
                cardsFaceUp: faceUpOnly(cards),
                total: cards.length > 0 ? String(total ?? '…') : null,
                won: baccarat!.outcome === spot,
                emptyLabel: state.phase === 'betting' ? 'Betting open' : 'Place your bets',
              })),
            }
          : isCraps
            ? {
                holdem: false,
                baccarat: false,
                roulette: false,
                craps: true,
                keno: false,
                label: crapsState!.point === null ? 'Come out' : `Point ${crapsState!.point}`,
                cardsFaceUp: [],
                valueLine:
                  crapsState!.lastRoll != null
                    ? `${crapsState!.lastRoll[0]} + ${crapsState!.lastRoll[1]} = ${rollTotal(
                        crapsState!.lastRoll,
                      )}`
                    : null,
                emptyLabel:
                  state.phase === 'betting'
                    ? 'Come-out betting'
                    : state.phase === 'rolling'
                      ? 'Dice are out'
                      : 'Hand settled',
                potLabel: null,
                coupHands: null,
              }
            : isThreecard
              ? {
                  holdem: false,
                  baccarat: false,
                  roulette: false,
                  craps: false,
                  keno: false,
                  label: 'Dealer',
                  cardsFaceUp: threecardState!.dealerCards.map((c) =>
                    c.faceUp ? c.card : undefined,
                  ),
                  valueLine:
                    threecardState!.dealerCards.length > 0
                      ? threecardState!.dealerLabel ??
                        `${threecardState!.dealerCards.length} cards down`
                      : null,
                  emptyLabel:
                    state.phase === 'betting'
                      ? 'Betting open'
                      : state.phase === 'playing'
                        ? 'Antes deciding'
                        : 'Place your antes',
                  potLabel: null,
                  coupHands: null,
                }
              : isKeno
                ? {
                    holdem: false,
                    baccarat: false,
                    roulette: false,
                    craps: false,
                    keno: true,
                    label: 'Board',
                    cardsFaceUp: [],
                    valueLine: null,
                    emptyLabel:
                      state.phase === 'betting'
                        ? 'Tap your spots'
                        : state.phase === 'dealer'
                          ? 'The balls are falling…'
                          : kenoState!.results
                            ? 'Draw settled'
                            : 'Place your bets',
                    potLabel: null,
                    coupHands: null,
                  }
                : {
              holdem: false,
              baccarat: false,
              roulette: false,
              craps: false,
              keno: false,
              label: 'Dealer',
              cardsFaceUp: faceUpOnly(state.dealer.cards),
              valueLine:
                state.dealer.cards.length > 0
                  ? `${dealerValue ?? '…'}${!state.dealer.revealed ? ' showing' : ''}`
                  : null,
              emptyLabel:
                state.phase === 'betting'
                  ? 'Betting open'
                  : state.phase === 'insurance'
                    ? 'Insurance?'
                    : 'Place your bets',
              potLabel: null,
              coupHands: null,
            }
    : {
        holdem: false,
        baccarat: false,
        roulette: false,
        craps: false,
        keno: false,
        label: 'Dealer',
        cardsFaceUp: [],
        valueLine: null,
        emptyLabel: 'Place your bets',
        potLabel: null,
        coupHands: null,
      };

  const holdemLine = state && isHoldem ? holdemStreetLine() : null;

  /**
   * The house board owns the screen while nobody is on the table — before the
   * first sit-down, or after the night wraps — and whenever the host puts the
   * leaderboard on the cast mid-session. A seated table keeps its felt between
   * rounds unless that toggle is on.
   */
  const forcedBoard = state?.leaderboardShown === true;
  const qrShown = state?.qrShown === true && !joinHidden;
  const showJoinCard = !joinHidden && (!tableActive || qrShown);
  const showFelt = tableActive && !forcedBoard && !qrShown;
  const stageLobby = !tableActive || forcedBoard || qrShown;
  const seriesByMember = new Map((timeline ?? []).map((s) => [s.memberId, s]));
  const lobbyBoard: HostLobbyBoard | null =
    casino && (state === null || !tableActive || forcedBoard)
      ? {
          casinoName: casino.name,
          // A bare TV's venue is this one table: a lineup would only repeat
          // the join card beside it.
          tables: casino.tables
            .filter((table) => table.status === 'open' && casino.adhoc !== true)
            .map((table) => {
            return {
              id: table.id,
              name: table.name,
              gameLabel: gameLabel(table.gameType),
              live: table.live,
              closed: table.status === 'closed',
              // A private venue prints what a table is doing, never the code
              // a stranger could type: phones in the room sit by tapping.
              // Closed tables never reach here — the lineup above drops them.
              note: joinHidden ? (table.live ? 'Playing' : 'Open') : table.code,
            };
          }),
          leaders: (leaderboard ?? []).map((entry, i) => {
            const extremes =
              entry.bestRound === 0 && entry.worstRound === 0
                ? null
                : `best ${formatSignedChips(entry.bestRound)} · worst ${formatSignedChips(entry.worstRound)}`;
            return {
              memberId: entry.memberId,
              rank: i + 1,
              name: entry.name,
              accentHex: entry.color ? profileHex(entry.color) : null,
              hasProfile: entry.hasProfile,
              netLabel: formatSignedChips(entry.net),
              handsLabel: `${entry.hands.toLocaleString()} ${entry.hands === 1 ? 'hand' : 'hands'}`,
              tone: entry.net > 0 ? 'win' : entry.net < 0 ? 'loss' : 'even',
              extremesLabel: extremes,
              wageredLabel: entry.wagered > 0 ? `${entry.wagered.toLocaleString()} out` : null,
              streakLabel:
                entry.winStreak > 0
                  ? `${entry.winStreak} straight ${entry.winStreak === 1 ? 'win' : 'wins'}`
                  : null,
            } satisfies HostLeaderRow;
          }),
          desks: (leaderboard ?? []).map((entry) =>
            boardDeskModel(entry, seriesByMember.get(entry.memberId) ?? null),
          ),
          emptyLine:
            (leaderboard ?? []).length === 0 ? 'No hands played yet tonight.' : null,
          forced: forcedBoard && tableActive,
        }
      : null;

  const seatsClosedUp = state !== null && state.phase !== 'lobby' && seatRows.some((r) => r.taken);
  const shownSeatRows = seatsClosedUp ? seatRows.filter((r) => r.taken) : seatRows;

  // The insurance wagers settle on the dealer's hole card, all at once. The
  // phones print each seat's own line; the felt reads one line for the rail.
  const insuranceLine = (() => {
    if (state?.gameType !== 'blackjack' || state.phase !== 'payout') return null;
    const insured = state.seats.filter((s) => (s.insuranceBet ?? 0) > 0);
    if (insured.length === 0) return null;
    const parts = insured.map((s) => {
      const who = s.name ?? `Seat ${s.index + 1}`;
      const net = (s.insurancePayout ?? 0) - (s.insuranceBet ?? 0);
      return `${who} ${formatSignedChips(net)}`;
    });
    return `Insurance · ${parts.join(' · ')}`;
  })();

  return {
    statusLine: statusLine(),
    seatRows,
    shownSeatRows,
    seatsClosedUp,
    dealerRow,
    holdemLine,
    holdemNarration: holdemNarration(),
    holdemResultLine: holdemResultLine(),
    winHeadline: winHeadline(wins),
    joinCodeLabel,
    joinUrl,
    joinUrlShort,
    castUrl,
    castBlocked,
    tableActive,
    banner: closed,
    kicker: casino && casino.adhoc !== true
      ? casino.name
      : isHoldem
        ? 'House party texas hold’em'
        : isBaccarat
          ? 'House party baccarat'
          : isRoulette
            ? 'House party roulette'
            : isCraps
              ? 'House party craps'
              : isThreecard
                ? 'House party three-card poker'
                : isKeno
                  ? 'House party keno'
                  : 'House party blackjack',
    hostName: host?.name ?? null,
    hostHasProfile: host?.hasProfile ?? false,
    // No table, nothing to mute: a pairing screen is not a muted screen.
    muted: state !== null && !state.soundOn,
    feltText: isHoldem
      ? `★ ${
          state!.holdemRules.bettingStructure === 'pot_limit' ? 'Pot-limit' : 'No-limit'
        } Texas Hold’em ★`
      : isRoulette
        ? `★ ${
            state!.rouletteRules.wheel === 'american' ? 'American' : 'European'
          } roulette ★`
        : isCraps
          ? `★ ${state!.crapsRules.maxOddsX}x odds ★`
          : isBaccarat
          ? `★ Tie pays ${state!.baccaratRules.tiePayout} to 1 ★`
          : isThreecard
            ? '★ Queen-high plays ★'
            : isKeno
              ? '★ Keno — twenty of eighty ★'
              : `★ Blackjack pays ${blackjackPayoutLabel(state?.rules.blackjackPayout ?? 1.5).replace(':', ' to ')} ★`,
    ruleLine: isHoldem
      ? `Blinds ${holdem?.smallBlind ?? state!.holdemRules.smallBlind} / ${
          holdem?.bigBlind ?? state!.holdemRules.bigBlind
        }${state!.holdemRules.ante > 0 ? ` · Ante ${state!.holdemRules.ante}` : ''}`
      : isRoulette
        ? `Straight pays 35 to 1 · outside bets even money or 2 to 1${
            state!.rouletteRules.laPartage ? ' · la partage on zeros' : ''
          }`
        : isCraps
          ? `Pass and don't pass pay even money · place 6 and 8 pay 7 to 6${
              state!.crapsRules.fieldTriple12 ? ' · field 12 pays 3 to 1' : ''
            }`
          : isBaccarat
          ? `Banker pays 1 to 1${
              state!.baccaratRules.commission > 0
                ? ` less ${(state!.baccaratRules.commission * 100).toFixed(0)}% commission`
                : ''
            }`
          : isThreecard
            ? `Ante pays 1 to 1 · ante bonus: straight 1:1, trips 4:1, straight flush 5:1${
                state!.threecardRules.pairPlus
                  ? ` · Pair Plus to ${state!.threecardRules.pairPlusMax.toLocaleString()}`
                  : ''
              }`
            : isKeno
              ? `Pick 1 to ${state!.kenoRules.maxSpots} spots · the house draws twenty${
                  state!.kenoRules.paytable !== 'classic'
                    ? ` · ${state!.kenoRules.paytable} slate`
                    : ''
                }`
              : `Dealer must stand on 17 and draw to 16${
              sideBetNames.length > 0
                ? ` · Side bets: ${sideBetNames.join(' · ')} (max ${
                    state?.rules.sideBetMax ?? 100
                  })`
                : ''
            }`,
    shoeLabel: state
      ? isRoulette
        ? `Spin · ${rouletteState?.spinNumber ?? state.round}`
        : isKeno
          ? `Draw · ${kenoState?.drawNumber ?? state.round}`
          : isCraps
          ? `Roll · ${crapsState?.rollNumber ?? 0}`
          : isHoldem
            ? `Hand · ${state.round}`
            : isThreecard
              ? `Hand · ${threecardState?.handNumber || state.round}`
              : `Shoe · ${state.shoeRemaining}`
      : '',
    historyLabel: !state
      ? null
      : isRoulette
        ? `Spin · ${rouletteState?.spinNumber ?? state.round}`
        : isCraps
          ? `Roll · ${crapsState?.rollNumber ?? 0}`
          : null,
    shoe: deckShoe(state),
    seatedCount: seated.length,
    maxSeats: MAX_SEATS,
    payoutNote: state?.phase === 'payout',
    payoutNoteLine:
      state?.phase === 'payout' ? "Next round deals from the host's phone." : null,
    insuranceLine,
    lobbyBoard,
    gamePoll: gamePollModel(state?.gamePoll, { canVote: false }),
    leaderboardShown: forcedBoard,
    qrShown,
    showJoinCard,
    showFelt,
    stageLobby,
    isHoldem,
    isBaccarat,
    isRoulette,
    isCraps,
    isThreecard,
    isKeno,
    beadRoad: (baccarat?.history ?? []).slice(-24).map((outcome) => ({
      outcome,
      letter: BEAD_LETTERS[outcome],
      label: SPOT_LABELS[outcome],
    })),
    threecardRoad: (threecardState?.history ?? []).slice(-24).map((outcome, i) => ({
      key: `${i}-${outcome}`,
      outcome,
      letter: OUTCOME_LETTERS[outcome],
      label: OUTCOME_NAMES[outcome],
    })),
    roulette: isRoulette && rouletteState
      ? (() => {
          const wheel = state!.rouletteRules.wheel;
          const order = wheelOrder(wheel);
          const stacks = rouletteStacks(rouletteState, state?.seats ?? [], null);
          // The pocket rides the wire at ball-release for the wheel's roll,
          // so the layout may not read it until the settle beat publishes the
          // results — a dolly or win flash mid-roll would call the spin early.
          const settled = rouletteState.results !== null;
          return {
            wheel,
            wheelOrder: order,
            landedIndex:
              rouletteState.pocket != null ? order.indexOf(rouletteState.pocket) : null,
            pocket: rouletteState.pocket != null ? String(rouletteState.pocket) : null,
            pocketValue: settled ? rouletteState.pocket : null,
            pocketColor:
              rouletteState.pocket != null ? pocketColor(rouletteState.pocket) : null,
            stacks,
            marks: settled ? rouletteWinMarks(rouletteState.pocket, stacks) : {},
            pocketRoad: rouletteState.history.slice(-18).map((p) => ({
              pocket: String(p),
              color: pocketColor(p),
            })),
            board: pocketBoard(rouletteState.history, order),
            resultLine: state?.phase === 'payout' ? rouletteResultLine() : null,
          };
        })()
      : null,
    keno:
      isKeno && kenoState
        ? (() => {
            const settled = kenoState.results !== null;
            const classicColors = state!.kenoRules.classicColors;
            // Every ticket at the table stacks on the felt in its own colour:
            // tickets number in seat order across the table, so each of a
            // seat's wagers is distinct and the swatches match the TV.
            const colorOf = (seatIndex: number, ticketIndex: number) =>
              kenoTicketColorIndex(kenoState.seats, seatIndex, ticketIndex);
            const history = kenoState.history.slice(-20);
            const tally = new Map<number, number>();
            for (const draw of history) {
              for (const spot of draw) tally.set(spot, (tally.get(spot) ?? 0) + 1);
            }
            return {
              draw: kenoState.draw ? [...kenoState.draw] : null,
              settled,
              endsAt: state?.deadline?.endsAt ?? null,
              classicColors,
              tickets: kenoState.seats.flatMap((ks) =>
                ks.tickets.map((t, i) => ({ spots: [...t.spots], colorIndex: colorOf(ks.index, i) })),
              ),
              seatSlates: kenoState.seats
                .filter((ks) => ks.tickets.length > 0)
                .map((ks) => ({
                  seatIndex: ks.index,
                  name: state?.seats[ks.index]?.name ?? null,
                  done: ks.done,
                  staked: ks.tickets.reduce((n, t) => n + t.amount, 0),
                  tickets: ks.tickets.map((t, i) => ({
                    id: t.id,
                    label: describeKenoTicket(t.spots),
                    amount: t.amount,
                    payout: t.payout,
                    catches: t.catches ?? [],
                    colorIndex: colorOf(ks.index, i),
                  })),
                })),
              history,
              hot: [...tally.entries()]
                .sort((a, b) => b[1] - a[1] || a[0] - b[0])
                .slice(0, 8)
                .map(([spot, count]) => ({ spot, count })),
            };
          })()
        : null,
    craps:
      isCraps && crapsState
        ? {
            rules: state!.crapsRules,
            stacks: crapsStacks(crapsState, state?.seats ?? [], null),
            marks: crapsMarks(crapsState),
            point: crapsState.point,
            puckLabel: crapsState.point === null ? 'COME OUT' : `POINT ${crapsState.point}`,
            puckOn: crapsState.point !== null,
            dice: crapsState.lastRoll,
            total: crapsState.lastRoll ? rollTotal(crapsState.lastRoll) : null,
            callLine: crapsRollCall(crapsState),
            shooterSeatIndex: crapsState.shooterSeatIndex,
            shooterName:
              crapsState.shooterSeatIndex != null
                ? state?.seats[crapsState.shooterSeatIndex]?.name ?? null
                : null,
            rollNumber: crapsState.rollNumber,
            rollRibbon: crapsState.rollHistory.slice(-12).map((dice, i) => ({
              key: `${i}-${dice[0]}${dice[1]}`,
              dice,
              total: rollTotal(dice),
              hard: isHardRoll(dice),
            })),
            resultLine: state?.phase === 'payout' ? crapsResultLine() : null,
            stats: {
              longest: crapsState.longestHand
                ? {
                    name:
                      crapsState.longestHand.seatIndex != null
                        ? state?.seats[crapsState.longestHand.seatIndex]?.name ?? null
                        : null,
                    rolls: crapsState.longestHand.rolls,
                  }
                : null,
              hot: crapsHotBoard(crapsState.totalTally),
            },
          }
        : null,
  };
}
